import { toast } from "sonner";
import { openReportPrint, buildReportPrintHtml } from "@/lib/reportPrint";

export function buildReportCsv(data, business = {}) {
    const lines = [];
    const escCsv = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const bizName = business.name || "Salon & Spa";
    lines.push(escCsv(bizName));
    lines.push("Revenue Report");
    lines.push(`Period,${data.period_start} to ${data.period_end} (${data.period_days} days)`);
    lines.push("");
    lines.push("Summary");
    lines.push(`Total Revenue,${data.total}`);
    lines.push(`Invoices,${data.count}`);
    lines.push(`Sub-total,${data.sub_total}`);
    lines.push(`GST,${data.gst_total}`);
    lines.push(`Discounts,${data.discount_total}`);
    lines.push(`New Customers,${data.new_customers}`);
    lines.push(`Memberships Sold,${data.memberships_sold}`);
    lines.push(`Membership Contract Value (period),${data.membership_contract_value ?? 0}`);
    lines.push(`Membership Collected (period),${data.membership_collected_in_period ?? 0}`);
    lines.push(`Membership Pending (new sales),${data.membership_pending_from_new_sales ?? 0}`);
    lines.push(`Membership Outstanding (all),${data.membership_outstanding_total ?? 0}`);
    lines.push(`Labor Cost,${data.labor_cost_total ?? 0}`);
    lines.push(`Labor Cost %,${data.labor_cost_pct ?? "N/A"}`);
    lines.push(`Appointments,${data.appointments_total}`);
    lines.push(`Previous Period Revenue,${data.prev_period_total}`);
    lines.push(`Growth %,${data.growth_pct ?? "N/A"}`);
    lines.push("");
    lines.push("Daily Revenue");
    lines.push("Date,Revenue,Invoices");
    data.by_day.forEach((d, i) => {
        lines.push(`${d.date},${d.revenue},${data.by_day_invoices[i]?.count ?? 0}`);
    });
    lines.push("");
    lines.push("Payment Modes");
    lines.push("Mode,Amount");
    data.by_payment.forEach((p) => lines.push(`${p.mode},${p.amount}`));
    lines.push("");
    lines.push("Top Services");
    lines.push("Service,Revenue,Qty");
    (data.by_service || []).forEach((s) => lines.push(`${escCsv(s.name)},${s.revenue},${s.count}`));
    lines.push("");
    lines.push("Revenue By Category");
    lines.push("Category,Amount");
    (data.by_revenue_type || []).forEach((t) => lines.push(`${escCsv(t.label)},${t.amount}`));
    lines.push("");
    lines.push("Membership Outstanding");
    lines.push("Customer,Mobile,Plan,Started,Total Due,Paid,Balance");
    (data.membership_outstanding || []).forEach((r) => {
        lines.push([
            escCsv(r.customer_name), r.customer_mobile, escCsv(r.plan_name),
            r.started_at || "", r.total_due, r.total_paid, r.balance_due,
        ].join(","));
    });
    lines.push("");
    lines.push("Staff Payroll");
    lines.push("Staff,Role,Pay Type,Revenue,Base,Commission,Total Pay");
    (data.staff_payroll || []).forEach((r) => {
        lines.push([
            escCsv(r.name), r.role, r.pay_type, r.revenue, r.base_salary, r.commission, r.total_earnings,
        ].join(","));
    });
    lines.push("");
    lines.push("Staff Revenue");
    lines.push("Staff,Amount");
    data.by_staff.forEach((s) => lines.push(`${escCsv(s.name)},${s.amount}`));
    lines.push("");
    lines.push("Invoice Ledger");
    lines.push("Invoice,Type,Customer,Mobile,Date,Payment,Sub-total,GST,Discount,Total");
    (data.ledger || []).forEach((r) => {
        lines.push([
            r.invoice_number, escCsv(r.sale_type_label || r.sale_type || ""), escCsv(r.customer_name), r.customer_mobile,
            r.created_at, r.payment_mode, r.sub_total,
            (r.cgst || 0) + (r.sgst || 0), r.discount, r.grand_total,
        ].join(","));
    });
    return lines.join("\n");
}

export function downloadReportCsv(data, business) {
    const csv = buildReportCsv(data, business);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-report-${data.period_start}-to-${data.period_end}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 3000);
    toast.success("Report downloaded");
}

export function printReportSummary(data, business) {
    const ok = openReportPrint(data, business);
    if (!ok) toast.error("Allow pop-ups to print the report");
    return ok;
}

function reportPdfFilename(data) {
    return `revenue-report-${data.period_start}-to-${data.period_end}.pdf`;
}

/** Download the formatted revenue report as a PDF file. */
export async function downloadReportPdf(data, business) {
    const fullHtml = buildReportPrintHtml(data, business).replace(/<script>[\s\S]*?<\/script>/i, "");
    const doc = new DOMParser().parseFromString(fullHtml, "text/html");
    const style = doc.querySelector("style")?.textContent || "";
    const page = doc.querySelector(".page");
    if (!page) {
        toast.error("Could not build report PDF");
        return false;
    }

    const host = document.createElement("div");
    host.style.position = "fixed";
    host.style.left = "-10000px";
    host.style.top = "0";
    host.style.width = "186mm";
    host.style.background = "#fff";

    const styleEl = document.createElement("style");
    styleEl.textContent = style;
    host.appendChild(styleEl);
    host.appendChild(page.cloneNode(true));
    document.body.appendChild(host);

    try {
        const { default: html2pdf } = await import("html2pdf.js");
        await html2pdf()
            .set({
                margin: [10, 10, 10, 10],
                filename: reportPdfFilename(data),
                image: { type: "jpeg", quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true, logging: false },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                pagebreak: { mode: ["avoid-all", "css", "legacy"] },
            })
            .from(host)
            .save();
        toast.success("Report downloaded as PDF");
        return true;
    } catch {
        toast.error("Could not download report PDF");
        return false;
    } finally {
        document.body.removeChild(host);
    }
}
